/**
 * Wrap a mutation handler so every per-app page doesn't have to repeat:
 *
 *   setBusy(true);
 *   setActionMessage(null);
 *   try {
 *     const result = await action();
 *     setActionMessage({ tone: 'success', text: success(result) });
 *     await reload();
 *   } catch (err) {
 *     setActionMessage({ tone: 'error', text: `${label}: ${apiMessage(err)}` });
 *   } finally {
 *     setBusy(false);
 *   }
 *
 * Callers provide:
 *   - `do()`             — the async operation
 *   - `onSuccess(result)`— success banner text (or omit for no banner)
 *   - `errorPrefix`      — prefix for error banner: "Publish failed", etc.
 *   - `setBusy`          — the page's busy state setter
 *   - `setMessage`       — the page's banner state setter
 *   - `reload?`          — optional refetch to run after success
 *
 * The error path always formats as `"${errorPrefix}: ${apiMessage(err)}"`
 * so all five manager pages produce a single, parseable error shape.
 */

import { apiMessage } from '../api/client.js';
import type { BannerTone } from '../components/ui.js';

export interface ActionMessage {
  tone: BannerTone;
  text: string;
}

export interface RunActionArgs<T> {
  do: () => Promise<T>;
  onSuccess?: (result: T) => string;
  errorPrefix: string;
  setBusy: (busy: boolean) => void;
  setMessage: (msg: ActionMessage | null) => void;
  reload?: () => Promise<void>;
}

export async function runAction<T>(args: RunActionArgs<T>): Promise<void> {
  args.setBusy(true);
  args.setMessage(null);
  try {
    const result = await args.do();
    if (args.onSuccess) {
      args.setMessage({ tone: 'success', text: args.onSuccess(result) });
    }
    if (args.reload) await args.reload();
  } catch (err) {
    args.setMessage({ tone: 'error', text: `${args.errorPrefix}: ${apiMessage(err)}` });
  } finally {
    args.setBusy(false);
  }
}
